import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Volume2, VolumeX, Play, Pause, Search, RefreshCw, ArrowRightLeft, Loader2, ChevronDown, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "all", "door", "phone", "footsteps", "nature", "vehicle", "paper",
  "impact", "animal", "ambient", "music", "voice", "action", "tech", "domestic", "other"
];

const SOURCE_COLORS = {
  mixkit: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  freesound: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  "freesound-cat": "bg-blue-500/10 text-blue-300 border-blue-500/15",
  "auto-rematch": "bg-amber-500/10 text-amber-400 border-amber-500/15",
  "category-fallback": "bg-gray-500/10 text-gray-400 border-gray-500/15",
};

export default function SfxPreviewPlayer({ seriesFilter }) {
  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Swap modal
  const [swapCue, setSwapCue] = useState(null);
  const [swapResults, setSwapResults] = useState([]);
  const [swapSearching, setSwapSearching] = useState(false);
  const [swapQuery, setSwapQuery] = useState("");
  const [swapPreviewId, setSwapPreviewId] = useState(null);

  // Freesound scraper
  const [fsScraping, setFsScraping] = useState(false);
  const [fsScrapeStatus, setFsScrapeStatus] = useState(null);

  const PAGE_SIZE = 50;

  const fetchCues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(page + 1) });
      if (catFilter !== "all") params.append("category", catFilter);
      if (searchTerm) params.append("search", searchTerm);
      const res = await axios.get(`${API}/sfx/library?${params}`);
      setCues(res.data.entries || []);
      setStats(res.data.stats || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [catFilter, searchTerm, page]);

  useEffect(() => { fetchCues(); }, [fetchCues]);
  useEffect(() => { setPage(0); }, [catFilter, searchTerm]);

  const playCue = (cue) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playingId === cue.cue_name) { setPlayingId(null); return; }
    if (!cue.audio_url) return;
    const audio = new Audio(`${BACKEND_URL}${cue.audio_url}`);
    audio.onended = () => setPlayingId(null);
    audio.play();
    audioRef.current = audio;
    setPlayingId(cue.cue_name);
  };

  const playUrl = (url) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const fullUrl = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
    const audio = new Audio(fullUrl);
    audio.onended = () => setSwapPreviewId(null);
    audio.play();
    audioRef.current = audio;
  };

  const openSwap = (cue) => {
    setSwapCue(cue);
    setSwapQuery(cue.cue_name.split(".")[0].trim().substring(0, 40));
    setSwapResults([]);
  };

  const searchSwap = async () => {
    if (!swapQuery.trim()) return;
    setSwapSearching(true);
    try {
      const res = await axios.get(`${API}/sfx/freesound/search?query=${encodeURIComponent(swapQuery)}&max_results=8`);
      setSwapResults(res.data.results || []);
    } catch (e) { console.error(e); }
    setSwapSearching(false);
  };

  const confirmSwap = async (newAudioUrl, soundId, source) => {
    if (!swapCue) return;
    try {
      await axios.put(`${API}/sfx/library/swap`, {
        cue_name: swapCue.cue_name,
        audio_url: newAudioUrl,
        matched_sound_id: soundId,
        source: source
      });
      setSwapCue(null);
      fetchCues();
    } catch (e) { console.error(e); alert("Swap failed"); }
  };

  const downloadAndSwap = async (item) => {
    try {
      const res = await axios.post(`${API}/sfx/freesound/download-one`, {
        mp3_url: item.mp3_url,
        title: item.title,
        freesound_id: item.freesound_id,
        category: swapCue?.category || "other"
      });
      confirmSwap(res.data.audio_url, res.data.id, "freesound");
    } catch (e) { console.error(e); alert("Download failed"); }
  };

  const startFreesoundScrape = async () => {
    setFsScraping(true);
    try {
      const res = await axios.post(`${API}/sfx/freesound/scrape`, { sounds_per_category: 5 });
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/sfx/scrape/${jobId}`);
          setFsScrapeStatus(s.data);
          if (s.data.status !== "running") { clearInterval(poll); setFsScraping(false); fetchCues(); }
        } catch { clearInterval(poll); setFsScraping(false); }
      }, 3000);
    } catch (e) { console.error(e); setFsScraping(false); }
  };

  return (
    <div className="space-y-4" data-testid="sfx-preview-player">
      {/* Stats bar */}
      {stats && (
        <div className="flex flex-wrap gap-3" data-testid="sfx-stats">
          <div className="px-3 py-2 bg-white/5 rounded text-center">
            <p className="text-lg font-bold text-amber-400">{stats.total}</p>
            <p className="text-[10px] text-gray-500 uppercase">Total Cues</p>
          </div>
          <div className="px-3 py-2 bg-white/5 rounded text-center">
            <p className="text-lg font-bold text-emerald-400">{stats.matched}</p>
            <p className="text-[10px] text-gray-500 uppercase">With Audio</p>
          </div>
          <div className="px-3 py-2 bg-white/5 rounded text-center">
            <p className="text-lg font-bold text-blue-400">{stats.freesound || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase">Freesound</p>
          </div>
          <div className="px-3 py-2 bg-white/5 rounded text-center">
            <p className="text-lg font-bold text-emerald-300">{stats.mixkit || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase">Mixkit</p>
          </div>
          <div className="px-3 py-2 bg-white/5 rounded text-center">
            <p className="text-lg font-bold">{stats.mp3_files || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase">MP3 Files</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Search cues..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-[#1a1a1a] border border-white/10 rounded text-sm text-white focus:outline-none focus:border-amber-500"
            data-testid="sfx-search-input" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-sm text-white focus:outline-none"
          data-testid="sfx-category-filter">
          {CATEGORIES.map(c => (
            <option key={c} value={c}>{c === "all" ? "All Categories" : c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
        <button onClick={startFreesoundScrape} disabled={fsScraping}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
          data-testid="freesound-scrape-btn">
          {fsScraping ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {fsScraping ? "Downloading..." : "Freesound"}
        </button>
        <button onClick={fetchCues}
          className="p-2 bg-white/5 hover:bg-white/10 rounded transition-colors" data-testid="sfx-refresh-btn">
          <RefreshCw size={14} className="text-gray-400" />
        </button>
      </div>

      {/* Freesound scrape status */}
      {fsScrapeStatus && fsScrapeStatus.status === "running" && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs">
          <div className="flex items-center justify-between mb-1">
            <span className="text-blue-400 font-semibold">Downloading from Freesound: {fsScrapeStatus.current_category}</span>
            <span className="text-gray-400">{fsScrapeStatus.downloaded} sounds</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1">
            <div className="bg-blue-500 h-1 rounded-full transition-all"
              style={{ width: `${(fsScrapeStatus.completed_categories?.length / (fsScrapeStatus.categories?.length || 1)) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Cue table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={24} className="text-amber-500 animate-spin" /></div>
      ) : (
        <div className="max-h-[500px] overflow-y-auto rounded-lg border border-white/5">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0d0d0d] z-10">
              <tr className="text-gray-500 uppercase border-b border-white/10">
                <th className="text-center py-2 px-2 w-10"></th>
                <th className="text-left py-2 px-2">Cue Description</th>
                <th className="text-left py-2 px-2 w-20">Category</th>
                <th className="text-left py-2 px-2 w-24">Source</th>
                <th className="text-center py-2 px-2 w-16">Swap</th>
              </tr>
            </thead>
            <tbody>
              {cues.map((cue, i) => (
                <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors"
                  data-testid={`sfx-cue-row-${i}`}>
                  <td className="py-1.5 px-2 text-center">
                    {cue.audio_url ? (
                      <button onClick={() => playCue(cue)}
                        className={`p-1 rounded transition-colors ${playingId === cue.cue_name ? "bg-amber-500/20 text-amber-400" : "text-gray-500 hover:text-white"}`}
                        data-testid={`play-cue-${i}`}>
                        {playingId === cue.cue_name ? <Pause size={12} /> : <Play size={12} />}
                      </button>
                    ) : (
                      <VolumeX size={12} className="text-gray-700 mx-auto" />
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-gray-300 truncate max-w-[300px]" title={cue.cue_name}>
                    {cue.display_name || cue.cue_name}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className="px-1.5 py-0.5 rounded bg-white/5 text-gray-400 text-[10px]">{cue.category}</span>
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={`px-1.5 py-0.5 rounded border text-[10px] ${SOURCE_COLORS[cue.source] || "bg-white/5 text-gray-400 border-white/10"}`}>
                      {cue.source || "none"}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <button onClick={() => openSwap(cue)}
                      className="p-1 text-gray-500 hover:text-blue-400 transition-colors"
                      data-testid={`swap-cue-${i}`}>
                      <ArrowRightLeft size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {stats && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, stats.filtered || stats.total)} of {stats.filtered || stats.total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 bg-white/5 rounded disabled:opacity-30 hover:bg-white/10">Prev</button>
            <button onClick={() => setPage(p => p + 1)} disabled={cues.length < PAGE_SIZE}
              className="px-3 py-1 bg-white/5 rounded disabled:opacity-30 hover:bg-white/10">Next</button>
          </div>
        </div>
      )}

      {/* Swap Modal */}
      {swapCue && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="swap-modal"
          onClick={() => setSwapCue(null)}>
          <div className="bg-[#111] border border-white/10 rounded-xl w-full max-w-lg p-5 space-y-4"
            onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="font-cinzel font-bold text-sm mb-1">Swap Sound</h3>
              <p className="text-xs text-gray-400 truncate">{swapCue.display_name || swapCue.cue_name}</p>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-500">{swapCue.category}</span>
            </div>

            {/* Current sound */}
            {swapCue.audio_url && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <button onClick={() => playCue(swapCue)} className="p-2 bg-amber-500/20 rounded text-amber-400">
                  <Play size={14} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-300">Current sound</p>
                  <p className="text-[10px] text-gray-500 truncate">{swapCue.source}</p>
                </div>
              </div>
            )}

            {/* Search Freesound */}
            <div className="flex gap-2">
              <input type="text" value={swapQuery} onChange={e => setSwapQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchSwap()}
                placeholder="Search Freesound..."
                className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                data-testid="swap-search-input" />
              <button onClick={searchSwap} disabled={swapSearching}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded disabled:opacity-50"
                data-testid="swap-search-btn">
                {swapSearching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[250px] overflow-y-auto space-y-1">
              {swapResults.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-white/[0.03] hover:bg-white/[0.06] rounded transition-colors"
                  data-testid={`swap-result-${i}`}>
                  <button onClick={() => { playUrl(item.mp3_url); setSwapPreviewId(item.freesound_id); }}
                    className={`p-1.5 rounded transition-colors ${swapPreviewId === item.freesound_id ? "bg-blue-500/20 text-blue-400" : "bg-white/5 text-gray-400 hover:text-white"}`}>
                    {swapPreviewId === item.freesound_id ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-300 truncate">{item.title}</p>
                    <p className="text-[10px] text-gray-500">{item.duration?.toFixed(1)}s</p>
                  </div>
                  <button onClick={() => downloadAndSwap(item)}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded transition-colors"
                    data-testid={`swap-use-${i}`}>
                    Use This
                  </button>
                </div>
              ))}
              {swapResults.length === 0 && !swapSearching && (
                <p className="text-center text-gray-500 text-xs py-4">Search Freesound to find replacement sounds</p>
              )}
            </div>

            <button onClick={() => setSwapCue(null)}
              className="w-full py-2 bg-white/5 hover:bg-white/10 text-gray-400 text-xs rounded transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
